import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Sparkles, RotateCw,
          Plus, Save, User, Package, Layers, Send, Info,
          AlertTriangle } from "lucide-react";
import ImageUpload from "../../components/ImageUpload";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SB = `${API}/academy/sandbox`;

/**
 * SandboxTask — renders the correct practical task UI based on task_id.
 * Task IDs (defined server-side in academy_sandbox.py):
 *   - create_client_anna
 *   - create_product
 *   - create_bundle_46
 *   - send_bundle_msg
 */
export default function SandboxTask({ taskId, onCompleted }) {
  if (taskId === "create_client_anna") return <TaskCreateClient onCompleted={onCompleted} />;
  if (taskId === "create_product")     return <TaskCreateProduct onCompleted={onCompleted} />;
  if (taskId === "create_bundle_46")   return <TaskCreateBundle onCompleted={onCompleted} />;
  if (taskId === "send_bundle_msg")    return <TaskSendMessage onCompleted={onCompleted} />;
  return <div style={{ padding: 20, color: "#7f1d1d" }}>Неизвестное задание</div>;
}

/* ============ Shared bits ============ */

const inputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 6,
  border: "1px solid #e2e0d7", fontSize: 13, fontFamily: "Manrope",
  background: "white", color: "#0A0A0A", boxSizing: "border-box",
};
const labelStyle = {
  display: "block", fontSize: 11, textTransform: "uppercase",
  letterSpacing: 0.5, color: "#8a8a89", marginBottom: 4, fontWeight: 700,
};
const btnPrimary = {
  background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
  border: "none", borderRadius: 8, padding: "12px 24px", fontWeight: 800,
  fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5, cursor: "pointer",
};
const btnGhost = {
  background: "white", border: "1px solid #d1d5db", color: "#0A0A0A",
  borderRadius: 8, padding: "10px 18px", fontWeight: 600, fontSize: 12,
  cursor: "pointer",
};

function ResultCard({ result, onRetry }) {
  if (!result) return null;
  const passed = result.passed;
  return (
    <div data-testid="sandbox-result-card"
      style={{
        marginTop: 20, padding: 18, borderRadius: 10,
        background: passed ? "#ecfdf5" : "#fffbea",
        border: `1px solid ${passed ? "#a7f3d0" : "#fde68a"}`,
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        {passed ? <CheckCircle2 size={20} color="#059669" />
                : <AlertTriangle size={20} color="#B45309" />}
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 24,
                       color: passed ? "#065f46" : "#78350f" }}>
          {result.score} / {result.max_score}
          {passed ? " · Отлично!" : " · Есть, что улучшить"}
        </div>
      </div>
      {result.xp_earned > 0 && (
        <div style={{ fontSize: 13, color: "#059669", marginBottom: 10 }}>
          <Sparkles size={13} style={{ display: "inline", verticalAlign: -2 }} /> +{result.xp_earned} XP
        </div>
      )}
      {result.breakdown && (
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          {["personalization", "clarity", "next_action"].map(k => (
            <div key={k} style={{ background: "white", padding: "6px 10px",
                                     borderRadius: 6, fontSize: 12,
                                     border: "1px solid #e2e0d7" }}>
              {({
                personalization: "Персонализация",
                clarity: "Понятность",
                next_action: "Призыв к шагу",
              })[k]}: <b>{result.breakdown[k]}/10</b>
            </div>
          ))}
        </div>
      )}
      {result.ai_feedback && (
        <div style={{ padding: 10, background: "white", borderRadius: 6,
                       border: "1px solid #e2e0d7", fontSize: 13,
                       color: "#0A0A0A", marginBottom: 12 }}>
          <b>Комментарий AI:</b> {result.ai_feedback}
        </div>
      )}
      {result.details && result.details.length > 0 && (
        <div>
          {result.details.map((d, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8,
                                       fontSize: 13, padding: "4px 0" }}>
              {d.ok ? <CheckCircle2 size={14} color="#059669" />
                    : <XCircle size={14} color="#B45309" />}
              <span style={{ color: d.ok ? "#065f46" : "#7f1d1d" }}>{d.label}</span>
            </div>
          ))}
        </div>
      )}
      {onRetry && (
        <button data-testid="sandbox-retry-btn"
          onClick={onRetry}
          style={{ ...btnGhost, marginTop: 12 }}>
          <RotateCw size={12} style={{ display: "inline", verticalAlign: -2, marginRight: 6 }} />
          Попробовать ещё раз
        </button>
      )}
    </div>
  );
}

async function fetchMe() {
  const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
  return r.data;
}

/* ============ TASK 1 — Create Client ============ */

function TaskCreateClient({ onCompleted }) {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", city: "", size: "", budget: "",
    interested_in: "", stage: "new", manager_id: "",
    comment: "", next_task: "", next_task_due: "",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchMe().then(u => { setMe(u); setForm(f => ({ ...f, manager_id: u.id || u._id })); });
  }, []);

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        ...form,
        budget: form.budget ? Number(form.budget) : null,
      };
      await axios.post(`${SB}/clients`, payload, { withCredentials: true });
    } catch (e) {
      setError(e.response?.data?.detail || "Ошибка сохранения");
    } finally { setSaving(false); }
  };

  const check = async () => {
    setSubmitting(true); setError(""); setResult(null);
    try {
      // Auto-save first (idempotent) so a fresh client is always the latest doc.
      await save();
      const r = await axios.post(`${SB}/tasks/create_client_anna/submit`,
        {}, { withCredentials: true });
      setResult(r.data);
      if (r.data.passed) onCompleted?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Ошибка проверки");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="sandbox-task-client"
      style={{ background: "white", padding: 22, borderRadius: 12,
                border: "1px solid #efeee9", marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <User size={18} color="#C5A059" />
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
          Карточка клиента
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Имя *</label>
          <input data-testid="sb-client-name" style={inputStyle}
            value={form.name} onChange={e => setField("name", e.target.value)}
            placeholder="Анна" />
        </div>
        <div>
          <label style={labelStyle}>Телефон</label>
          <input data-testid="sb-client-phone" style={inputStyle}
            value={form.phone} onChange={e => setField("phone", e.target.value)}
            placeholder="+7 ..." />
        </div>
        <div>
          <label style={labelStyle}>Город *</label>
          <input data-testid="sb-client-city" style={inputStyle}
            value={form.city} onChange={e => setField("city", e.target.value)}
            placeholder="Алматы" />
        </div>
        <div>
          <label style={labelStyle}>Размер *</label>
          <input data-testid="sb-client-size" style={inputStyle}
            value={form.size} onChange={e => setField("size", e.target.value)}
            placeholder="44" />
        </div>
        <div>
          <label style={labelStyle}>Бюджет, ₸ *</label>
          <input data-testid="sb-client-budget" style={inputStyle} type="number"
            value={form.budget} onChange={e => setField("budget", e.target.value)}
            placeholder="700000" />
        </div>
        <div>
          <label style={labelStyle}>Этап воронки *</label>
          <select data-testid="sb-client-stage" style={inputStyle}
            value={form.stage} onChange={e => setField("stage", e.target.value)}>
            <option value="new">Новый</option>
            <option value="contacted">Контакт установлен</option>
            <option value="thinking">Думает</option>
            <option value="appointment">Записан на примерку</option>
            <option value="purchased">Купил</option>
            <option value="lost">Потерян</option>
          </select>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Что ищет *</label>
          <input data-testid="sb-client-interested" style={inputStyle}
            value={form.interested_in}
            onChange={e => setField("interested_in", e.target.value)}
            placeholder="Кашемировое пальто" />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Ответственный менеджер *</label>
          <div style={{ padding: "10px 12px", borderRadius: 6,
                         border: "1px solid #e2e0d7", background: "#f9fafb",
                         fontSize: 13, color: "#0A0A0A" }}
               data-testid="sb-client-manager">
            {me?.name || me?.email || "…"}
            <span style={{ marginLeft: 8, color: "#059669", fontSize: 11 }}>
              (вы автоматически назначены)
            </span>
          </div>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Комментарий</label>
          <textarea data-testid="sb-client-comment" style={{ ...inputStyle, minHeight: 60 }}
            value={form.comment} onChange={e => setField("comment", e.target.value)}
            placeholder="Клиентка написала в WhatsApp, хочет светлое пальто..." />
        </div>
        <div>
          <label style={labelStyle}>Следующая задача *</label>
          <input data-testid="sb-client-next-task" style={inputStyle}
            value={form.next_task} onChange={e => setField("next_task", e.target.value)}
            placeholder="Отправить 3 варианта пальто" />
        </div>
        <div>
          <label style={labelStyle}>Когда *</label>
          <input data-testid="sb-client-next-due" style={inputStyle}
            type="datetime-local"
            value={form.next_task_due}
            onChange={e => setField("next_task_due", e.target.value)} />
        </div>
      </div>

      {error && <div style={{ color: "#7f1d1d", fontSize: 13, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <button data-testid="sb-client-save" onClick={save} disabled={saving} style={btnGhost}>
          <Save size={12} style={{ display: "inline", verticalAlign: -2, marginRight: 6 }} />
          {saving ? "Сохраняем..." : "Сохранить"}
        </button>
        <button data-testid="sb-client-check" onClick={check} disabled={submitting} style={btnPrimary}>
          {submitting ? <Loader2 size={12} className="animate-spin"
                                    style={{ display: "inline", verticalAlign: -2, marginRight: 6 }} /> : null}
          Проверить задание
        </button>
      </div>

      <ResultCard result={result} onRetry={() => setResult(null)} />
    </div>
  );
}

/* ============ TASK 2 — Create Product ============ */

const PRODUCT_CATEGORIES = [
  "Пальто", "Кашемировое пальто", "Пальто с мехом",
  "Пуховик", "Шуба", "Плащ", "Тренч", "Куртка", "Жакет",
];

function TaskCreateProduct({ onCompleted }) {
  const [form, setForm] = useState({
    name: "", category: "Пальто", material: "", sizes: [], price: "",
    color: "", images: [], description: "",
  });
  const [sizeInput, setSizeInput] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm({ ...form, [k]: v });
  const addSize = () => {
    // Trainees often type «42,44,46» in one go — split into separate chips.
    const parts = sizeInput.split(/[,;/|\s]+/).map(x => x.trim()).filter(Boolean);
    const next = [...form.sizes];
    parts.forEach(p => { if (!next.includes(p)) next.push(p); });
    if (next.length === form.sizes.length) { setSizeInput(""); return; }
    setForm({ ...form, sizes: next });
    setSizeInput("");
  };
  const removeSize = (s) => setForm({ ...form, sizes: form.sizes.filter(x => x !== s) });

  const buildPayload = () => ({
    name: form.name, category: form.category, material: form.material,
    sizes: form.sizes, price: form.price ? Number(form.price) : null,
    color: form.color,
    image_url: form.images[0] || "",  // sandbox stores only 1 image
    description: form.description,
  });

  const save = async () => {
    setSaving(true); setError("");
    try {
      await axios.post(`${SB}/products`, buildPayload(), { withCredentials: true });
    } catch (e) {
      setError(e.response?.data?.detail || "Ошибка сохранения");
    } finally { setSaving(false); }
  };

  const check = async () => {
    setSubmitting(true); setError(""); setResult(null);
    try {
      await save();
      const r = await axios.post(`${SB}/tasks/create_product/submit`,
        {}, { withCredentials: true });
      setResult(r.data);
      if (r.data.passed) onCompleted?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Ошибка проверки");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="sandbox-task-product"
      style={{ background: "white", padding: 22, borderRadius: 12,
                border: "1px solid #efeee9", marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Package size={18} color="#C5A059" />
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
          Карточка товара
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Название *</label>
          <input data-testid="sb-product-name" style={inputStyle}
            value={form.name} onChange={e => setField("name", e.target.value)}
            placeholder='Пальто "Marina" молочный кашемир' />
        </div>
        <div>
          <label style={labelStyle}>Категория *</label>
          <select data-testid="sb-product-category" style={inputStyle}
            value={form.category} onChange={e => setField("category", e.target.value)}>
            {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Материал *</label>
          <input data-testid="sb-product-material" style={inputStyle}
            value={form.material} onChange={e => setField("material", e.target.value)}
            placeholder="100% кашемир" />
        </div>
        <div>
          <label style={labelStyle}>Цена, ₸ *</label>
          <input data-testid="sb-product-price" style={inputStyle} type="number"
            value={form.price} onChange={e => setField("price", e.target.value)}
            placeholder="690000" />
        </div>
        <div>
          <label style={labelStyle}>Цвет</label>
          <input data-testid="sb-product-color" style={inputStyle}
            value={form.color} onChange={e => setField("color", e.target.value)}
            placeholder="молочный светлый" />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Размеры *</label>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {form.sizes.map(s => (
              <span key={s} style={{ padding: "4px 8px", background: "#f3f4f6",
                                     borderRadius: 4, fontSize: 12 }}>
                {s} <button onClick={() => removeSize(s)}
                  style={{ background: "transparent", border: "none",
                            cursor: "pointer", color: "#7f1d1d", marginLeft: 3 }}>×</button>
              </span>
            ))}
            <input data-testid="sb-product-size-input" style={{ ...inputStyle, width: 130 }}
              value={sizeInput} onChange={e => setSizeInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSize(); } }}
              placeholder="44 или 42,44,46" />
            <button data-testid="sb-product-add-size" onClick={addSize} style={btnGhost}>
              <Plus size={12} /> добавить
            </button>
          </div>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Фото товара *</label>
          <ImageUpload
            value={form.images}
            onChange={(imgs) => setField("images", imgs)}
            maxImages={1}
            testId="sb-product-image"
            label=""
            uploadUrl={`${SB}/upload`}
          />
          <div style={{ fontSize: 11, color: "#8a8a89", marginTop: 6 }}>
            Загрузите фото, подходящее под описание: пальто → фото пальто, пуховик → фото пуховика, шуба → фото шубы.
          </div>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Описание * (от 20 символов)</label>
          <textarea data-testid="sb-product-description"
            style={{ ...inputStyle, minHeight: 80 }}
            value={form.description}
            onChange={e => setField("description", e.target.value)}
            placeholder="Двубортное кашемировое пальто в оттенке молочного айвори. Лёгкое, идеально под платья и костюмы." />
        </div>
      </div>

      {error && <div style={{ color: "#7f1d1d", fontSize: 13, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <button data-testid="sb-product-save" onClick={save} disabled={saving} style={btnGhost}>
          <Save size={12} style={{ display: "inline", verticalAlign: -2, marginRight: 6 }} />
          {saving ? "Сохраняем..." : "Сохранить"}
        </button>
        <button data-testid="sb-product-check" onClick={check} disabled={submitting} style={btnPrimary}>
          {submitting ? <Loader2 size={12} className="animate-spin"
                                    style={{ display: "inline", verticalAlign: -2, marginRight: 6 }} /> : null}
          Проверить задание
        </button>
      </div>

      <ResultCard result={result} onRetry={() => setResult(null)} />
    </div>
  );
}

/* ============ TASK 3 — Create Bundle ============ */

function TaskCreateBundle({ onCompleted }) {
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fixtureLoaded, setFixtureLoaded] = useState(false);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${SB}/products`, { withCredentials: true });
      // Prefer fixtures (with _fixture:true), but if user has no products at all, show all.
      const all = r.data.products || [];
      // Backend returns _fixture flag internally; keep all here (fixtures + own).
      setProducts(all);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadProducts(); }, []);

  const seedFixture = async () => {
    setLoading(true); setError("");
    try {
      await axios.post(`${SB}/products/seed-fixture`, {}, { withCredentials: true });
      setFixtureLoaded(true);
      await loadProducts();
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось загрузить каталог");
    } finally { setLoading(false); }
  };

  const toggle = (id) => {
    setSelectedIds(sel => sel.includes(id)
      ? sel.filter(x => x !== id)
      : (sel.length >= 3 ? sel : [...sel, id]));
  };

  const check = async () => {
    setSubmitting(true); setError(""); setResult(null);
    try {
      await axios.post(`${SB}/bundles`, {
        product_ids: selectedIds,
        target_size: "46", target_budget: 900000,
      }, { withCredentials: true });
      const r = await axios.post(`${SB}/tasks/create_bundle_46/submit`,
        {}, { withCredentials: true });
      setResult(r.data);
      if (r.data.passed) onCompleted?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Ошибка проверки");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="sandbox-task-bundle"
      style={{ background: "white", padding: 22, borderRadius: 12,
                border: "1px solid #efeee9", marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center",
                    justifyContent: "space-between", marginBottom: 14, gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Layers size={18} color="#C5A059" />
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
            Подборка для клиента
          </div>
        </div>
        <button data-testid="sb-bundle-load-catalog" onClick={seedFixture} style={btnGhost}>
          {fixtureLoaded ? "Каталог загружен ✓" : "Загрузить каталог"}
        </button>
      </div>

      <div style={{ padding: 12, background: "#fef3c7", borderRadius: 8,
                     marginBottom: 14, fontSize: 13, color: "#78350f",
                     display: "flex", gap: 8, alignItems: "flex-start" }}>
        <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <b>Клиент:</b> размер 46, бюджет до 900 000 ₸, ищет пальто, светлые оттенки.
          Выберите ровно <b>3 модели</b>.
          Выбрано: <b>{selectedIds.length}/3</b>
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: 20 }}>
        <Loader2 size={20} className="animate-spin" style={{ display: "inline" }} />
      </div>}

      {!loading && products.length === 0 && (
        <div style={{ textAlign: "center", padding: 30, color: "#8a8a89" }}>
          Пока пусто. Нажмите «Загрузить каталог».
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                     gap: 12 }}>
        {products.map(p => {
          const isSelected = selectedIds.includes(p.id);
          const sizes = (p.sizes || []).flatMap(s => String(s).split(/[,;/|\s]+/)).filter(Boolean);
          const issues = [];
          if (!sizes.includes("46")) issues.push("нет размера 46");
          if ((p.price || 0) > 900000) issues.push("дороже 900 000 ₸");
          if (!/пальт|плащ|тренч|coat/i.test(p.category || "")) issues.push("не пальто");
          return (
            <div key={p.id} data-testid={`sb-product-tile-${p.id}`}
              onClick={() => toggle(p.id)}
              style={{
                border: `2px solid ${isSelected ? "#C5A059" : "#efeee9"}`,
                borderRadius: 10, padding: 10, cursor: "pointer",
                background: isSelected ? "rgba(197,160,89,0.06)" : "white",
                transition: "all .15s",
              }}>
              {p.image_url && (
                <img src={p.image_url} alt={p.name}
                  style={{ width: "100%", height: 140, objectFit: "cover",
                            borderRadius: 6, marginBottom: 8 }}
                  onError={e => e.target.style.display = "none"} />
              )}
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0A0A0A",
                             minHeight: 32, lineHeight: 1.3 }}>
                {p.name}
              </div>
              <div style={{ fontSize: 11, color: "#8a8a89", marginTop: 4 }}>
                {p.category} · {p.color || "цвет не указан"}
              </div>
              <div style={{ fontSize: 12, color: "#8a8a89", marginTop: 2 }}>
                Размеры: {(p.sizes || []).join(", ") || "—"}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0A", marginTop: 4 }}>
                {p.price ? p.price.toLocaleString("ru") + " ₸" : "—"}
              </div>
              {issues.length > 0 && (
                <div data-testid={`sb-product-issue-${p.id}`}
                  style={{ marginTop: 6, fontSize: 10, color: "#B45309",
                            background: "#fffbea", border: "1px solid #fde68a",
                            borderRadius: 4, padding: "3px 6px" }}>
                  Не подходит клиенту: {issues.join(", ")}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <div style={{ color: "#7f1d1d", fontSize: 13, marginTop: 10 }}>{error}</div>}

      <button data-testid="sb-bundle-check" onClick={check}
        disabled={submitting || selectedIds.length !== 3}
        style={{ ...btnPrimary, marginTop: 14,
                  opacity: selectedIds.length === 3 ? 1 : 0.5 }}>
        Проверить подборку
      </button>

      <ResultCard result={result} onRetry={() => { setResult(null); setSelectedIds([]); }} />
    </div>
  );
}

/* ============ TASK 4 — Send Bundle Message ============ */

function TaskSendMessage({ onCompleted }) {
  const [bundles, setBundles] = useState([]);
  const [message, setMessage] = useState(
    "Анна, добрый день! Подобрала для вас 3 модели пальто размера 46 в светлых оттенках, все в вашем бюджете. Какая ближе по стилю — заеду с ними в бутик на примерку сегодня после 17:00 или завтра днём?"
  );
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${SB}/bundles`, { withCredentials: true })
      .then(r => setBundles(r.data.bundles || []))
      .catch(() => {});
  }, []);

  const latest = bundles[0];

  const check = async () => {
    setSubmitting(true); setError(""); setResult(null);
    try {
      if (!latest) {
        setError("Сначала выполните Задание 3 — соберите подборку.");
        setSubmitting(false); return;
      }
      // Persist a fresh bundle with new message (so latest bundle carries the message).
      await axios.post(`${SB}/bundles`, {
        product_ids: latest.product_ids,
        target_size: latest.target_size,
        target_budget: latest.target_budget,
        message,
      }, { withCredentials: true });
      const r = await axios.post(`${SB}/tasks/send_bundle_msg/submit`,
        {}, { withCredentials: true });
      setResult(r.data);
      if (r.data.passed) onCompleted?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Ошибка проверки");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="sandbox-task-message"
      style={{ background: "white", padding: 22, borderRadius: 12,
                border: "1px solid #efeee9", marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Send size={18} color="#C5A059" />
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
          Сопроводительное сообщение
        </div>
      </div>

      {!latest && (
        <div style={{ padding: 12, background: "#fef2f2", borderRadius: 8,
                       marginBottom: 12, fontSize: 13, color: "#7f1d1d" }}>
          <AlertTriangle size={14} style={{ display: "inline", verticalAlign: -2, marginRight: 6 }} />
          Сначала выполните Задание 3 — соберите подборку из 3 моделей.
        </div>
      )}

      {latest && (
        <div style={{ padding: 12, background: "#f9fafb", borderRadius: 8,
                       marginBottom: 12, fontSize: 12, color: "#64748b" }}>
          Ваша подборка: <b>{(latest.products || []).map(p => p.name).join(" · ")}</b>
        </div>
      )}

      <label style={labelStyle}>Ваше сообщение клиенту</label>
      <textarea data-testid="sb-msg-textarea"
        style={{ ...inputStyle, minHeight: 160, fontFamily: "Manrope" }}
        value={message} onChange={e => setMessage(e.target.value)}
        placeholder="Напишите, как будто отправляете в WhatsApp..." />

      <div style={{ fontSize: 11, color: "#8a8a89", marginTop: 4 }}>
        {message.length} символов
      </div>

      {error && <div style={{ color: "#7f1d1d", fontSize: 13, marginTop: 8 }}>{error}</div>}

      <button data-testid="sb-msg-check" onClick={check} disabled={submitting || !latest}
        style={{ ...btnPrimary, marginTop: 12 }}>
        {submitting ? <Loader2 size={12} className="animate-spin"
                                  style={{ display: "inline", verticalAlign: -2, marginRight: 6 }} /> : null}
        Отправить на проверку AI
      </button>

      <ResultCard result={result} onRetry={() => setResult(null)} />
    </div>
  );
}
